// API Routes Configuration
export const API_ROUTES = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
  },

  // Users
  USERS: {
    BASE: '/users',
    PROFILE: '/users/profile',
    CLIENT_PORTAL_PUBLIC: (shareToken: string) => `/users/client-portal/${shareToken}`,
    UPDATE_AVATAR: '/users/avatar',
    UPDATE_PASSWORD: '/users/password',
    BACKUPS: '/users/backups',
    BACKUPS_EXPORT: '/users/backups/export',
    BACKUPS_IMPORT: '/users/backups/import',
    EMAIL_VERIFICATION_SEND: '/users/email-verification-code/send',
    EMAIL_VERIFICATION_VERIFY: '/users/email-verification-code/verify',
  },

  CLIENT_PORTALS: {
    BASE: '/client-portals',
    PROJECT: (projectId: string) => `/client-portals/projects/${encodeURIComponent(projectId)}`,
    BY_SHARE_TOKEN: (shareToken: string) => `/client-portals/${shareToken}`,
    QUOTE_RESPONSE: (shareToken: string) => `/client-portals/${shareToken}/quote-response`,
    SIGN_CONTRACT: (shareToken: string) => `/client-portals/${shareToken}/sign-contract`,
    QUESTIONNAIRE: (shareToken: string) => `/client-portals/${shareToken}/questionnaire`,
    PAYMENT_PLAN: (shareToken: string) => `/client-portals/${shareToken}/payment-plan`,
    PAYMENT_REQUEST: (shareToken: string) => `/client-portals/${shareToken}/payment-request`,
  },

  // Licenses
  LICENSES: {
    BASE: '/licenses',
    VALIDATE: '/licenses/validate',
    ACTIVATE: '/licenses/activate',
    DEACTIVATE: '/licenses/deactivate',
  },

  // Conversations
  CONVERSATIONS: {
    BASE: '/conversations',
    BY_ID: (id: string) => `/conversations/${id}`,
    MESSAGES: (id: string) => `/conversations/${id}/messages`,
  },

  // Contacts/Leads
  CONTACTS: {
    BASE: '/contacts',
    CREATE_MANUAL: '/contacts/manual',
    BY_ID: (id: string) => `/contacts/${id}`,
    EXPORT: '/contacts/export',
  },

  CLIENTS: {
    BASE: '/clients',
    SYNC: '/clients/sync',
    MERGE: '/clients/merge',
    SPLIT_CONTACT: (id: string) => `/clients/${id}/split-contact`,
  },

  // Projects
  PROJECTS: {
    BASE: '/projects',
    ALL_INVOICES: '/projects/invoices/all',
    BY_ID: (id: string) => `/projects/${id}`,
    RESTORE: (id: string) => `/projects/${id}/restore`,
    PERMANENT_DELETE: (id: string) => `/projects/${id}/permanent`,
    INVOICES: (projectId: string) => `/projects/${projectId}/invoices`,
    INVOICE_BY_ID: (projectId: string, invoiceId: string) =>
      `/projects/${projectId}/invoices/${invoiceId}`,
    FORMS: (projectId: string) => `/projects/${projectId}/forms`,
    FORM_BY_ID: (projectId: string, formId: string) => `/projects/${projectId}/forms/${formId}`,
    ASSIGNMENTS: (projectId: string) => `/projects/${projectId}/assignments`,
  },

  TEAM: {
    MEMBERS: '/team/members',
    MEMBER_BY_ID: (membershipId: string) => `/team/members/${membershipId}`,
    CLAIM_INVITATIONS: '/team/invitations/claim',
  },

  ORCHESTRATOR: {
    PROJECT_MESSAGES: (projectId: string) => `/orchestrator/${projectId}/messages`,
    PENDING_DRAFTS: '/orchestrator/pending-drafts',
    RESOLVE_PENDING: (projectId: string, actionId: string) =>
      `/orchestrator/${projectId}/pending/${actionId}`,
  },

  // Workflows / Automation
  WORKFLOWS: {
    TEMPLATES: '/workflows/templates',
    TEMPLATE_BY_ID: (id: string) => `/workflows/templates/${id}`,
    FLOWS: '/workflows/flows',
    FLOW_STATUS: (flowId: string) => `/workflows/flows/${flowId}/status`,
    FLOW_STEP_STATE: (flowId: string, stepId: string) =>
      `/workflows/flows/${flowId}/steps/${stepId}`,
    // alias used by updateFlowTemplate thunk
    UPDATE_TEMPLATE: (id: string) => `/workflows/templates/${id}`,
    GENERATE_EMAIL: '/workflows/generate-email',
    ANALYZE_MESSAGE: '/workflows/analyze-message',
    GENERATE_REPLY: '/workflows/generate-reply',
    EXTRACT_CHEAT_SHEET: '/workflows/extract-cheat-sheet',
    INTELLIGENCE_CYCLE: '/workflows/intelligence-cycle',
    TEMPLATES_RESET: '/workflows/templates/reset',
    PROJECT_TEMPLATE: (projectId: string) => `/workflows/projects/${projectId}/template`,
    PROJECT_TEMPLATE_RESET: (projectId: string) =>
      `/workflows/projects/${projectId}/template/reset`,
    PROJECT_WORKFLOW_BY_KEY: (projectId: string, workflowKey: string) =>
      `/workflows/projects/${projectId}/workflows/${workflowKey}`,
    PROJECT_STEP_TRIGGER_NOW: (projectId: string, stepId: string) =>
      `/workflows/projects/${projectId}/steps/${stepId}/trigger-now`,
  },

  // Invoice Packages (templates)
  PACKAGES: {
    BASE: '/packages',
    BY_ID: (id: string) => `/packages/${id}`,
  },

  // Widget Settings
  WIDGET: {
    BASE: '/widget',
    SETTINGS: '/widget/settings',
    EMBED_CODE: '/widget/embed-code',
  },

  // Training Data (AI Knowledge Base)
  TRAINING_DATA: {
    BASE: '/training-data',
    SECTION: (key: string) => `/training-data/section/${key}`,
    TEMPLATES: '/training-data/templates',
    TEMPLATE_BY_ID: (id: string) => `/training-data/templates/${id}`,
    RESPONSES: '/training-data/responses',
    RESPONSE_BY_ID: (id: string) => `/training-data/responses/${id}`,
  },

  SCHEDULER: {
    TEMPLATES: '/scheduler/templates',
    TEMPLATE_BY_ID: (id: string) => `/scheduler/templates/${id}`,
    SETTINGS: '/scheduler/settings',
    SETTINGS_BY_PROJECT: (projectId: string) => `/scheduler/settings/${projectId}`,
  },

  // Subscriptions & Billing
  BILLING: {
    BASE: '/billing',
    SUBSCRIPTIONS: '/billing/subscriptions',
    INVOICES: '/billing/invoices',
    PAYMENT_METHODS: '/billing/payment-methods',
    CREATE_CHECKOUT: '/billing/create-checkout-session',
    CREATE_PORTAL: '/billing/create-portal',
    STRIPE_CONNECT_STATUS: '/billing/stripe-connect/status',
    STRIPE_CONNECT_LINK: '/billing/stripe-connect/link',
    STRIPE_CONNECT_DISCONNECT: '/billing/stripe-connect/disconnect',
  },

  // Sync utilities
  SYNC: {
    SUBSCRIPTION: '/sync/sync-subscription',
  },

  // Integrations
  INTEGRATIONS: {
    BASE: '/integrations',
    REQUEST: '/integrations/request',
    REQUESTS: '/integrations/requests',
    REQUEST_BY_ID: (id: string) => `/integrations/requests/${id}`,
    GOOGLE_CALENDAR: '/integrations/google-calendar',
    STRIPE: '/integrations/stripe',
  },

  SUPPORT: {
    FEEDBACK: '/support/feedback',
    FEEDBACK_BY_ID: (id: string) => `/support/feedback/${id}`,
    ADMIN_FEEDBACK: '/admin/support/feedback',
    ADMIN_FEEDBACK_BY_ID: (id: string) => `/admin/support/feedback/${id}`,
  },

  // Admin
  ADMIN: {
    USERS: '/admin/users',
    LICENSES: '/admin/licenses',
    ROADMAP: '/admin/roadmap',
    CHANGELOG: '/admin/changelog',
    CONTENT: '/admin/content',
    ANALYTICS: '/admin/analytics',
  },

  // OpenAI
  AI: {
    CHAT: '/ai/chat',
    COMPLETE: '/ai/complete',
  },

  // Email
  EMAIL: {
    SEND: '/email/send',
    SCHEDULE: '/email/schedule',
    SCHEDULED_LIST: '/email/scheduled',
    SCHEDULED_DELETE: (id: string) => `/email/scheduled/${id}`,
    VERIFY: '/email/verify',
    SAVE: '/email/save',
    SETTINGS: '/email/settings',
    FETCH: '/email/fetch',
    SYNC: '/email/sync',
  },

  // Labels (synced across web & mobile)
  LABELS: {
    BASE: '/labels',
    RENAME: '/labels/rename',
    DELETE: (name: string) => `/labels/${encodeURIComponent(name)}`,
    TOGGLE: '/labels/toggle',
  },

  NOTIFICATIONS: {
    BASE: '/notifications',
    BY_ID: (id: string) => `/notifications/${id}`,
    READ: (id: string) => `/notifications/${id}/read`,
    ACTION: (id: string) => `/notifications/${id}/action`,
    READ_ALL: '/notifications/read-all',
    BULK_DELETE: '/notifications/delete',
    SETTINGS: '/notifications/settings',
    REGISTER_DEVICE: '/notifications/register-device',
    UNREGISTER_DEVICE: '/notifications/unregister-device',
  },

  PUBLIC: {
    INVOICE_BY_SHARE_TOKEN: (shareToken: string) => `/public/invoices/${shareToken}`,
    INVOICE_PAYMENT: (shareToken: string) => `/public/invoices/${shareToken}/pay`,
    FORM_BY_SHARE_TOKEN: (shareToken: string) => `/public/forms/${shareToken}`,
    SIGN_FORM: (shareToken: string) => `/public/forms/${shareToken}/sign`,
  },
};

// App Routes (Frontend)
export const APP_ROUTES = {
  HOME: '/',

  // Authentication
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  EMAIL_VERIFIED_SUCCESS: '/email-verified',
  ONBOARDING: '/onboarding',
  PLAN_SELECTION: '/plan-selection',

  // Dashboard
  DASHBOARD: '/dashboard',
  FORMS_LEGACY: '/dashboard/forms',
  CONVERSATIONS: '/dashboard/conversations',
  CONVERSATION_DETAIL: '/dashboard/conversations/:id', // PLANNED: not yet routed
  EMAIL: '/dashboard/email',
  EMAIL_SETUP: '/dashboard/email?configure=1',
  INVOICING_LEGACY: '/dashboard/invoicing',
  PROJECTS: '/dashboard/projects',
  CLIENTS: '/dashboard/clients',
  CONTACTS_LEGACY: '/dashboard/contacts',
  BOOKINGS: '/dashboard/bookings',
  BOOKINGS_WITH_TAB: (tab: string) => `/dashboard/bookings?tab=${tab}`,
  CALENDAR: '/dashboard/calendar',
  CALENDAR_SETTINGS: '/dashboard/calendar?settings=1',
  NOTIFICATIONS: '/dashboard/notifications',
  SUPPORT: '/dashboard/support',
  TEMPLATES: '/dashboard/templates',
  SETTINGS: '/dashboard/settings',
  SETTINGS_WITH_SECTION: (section: string) => `/dashboard/settings?section=${section}`,
  WORKFLOW: '/dashboard/workflow',
  INTEGRATIONS: '/dashboard/integrations',
  INTEGRATIONS_WITH_MODAL: (modal: string) => `/dashboard/integrations?modal=${modal}`, // PLANNED: not yet used
  BILLING: '/dashboard/billing',
  WIDGET_SETTINGS: '/dashboard/widget',
  WIDGET_CODE: '/dashboard/widget/code', // PLANNED: not yet routed
  AI_TRAINING: '/dashboard/settings?section=ai-training',
  AI_TRAINING_TEMPLATES: '/dashboard/settings?section=ai-training&tab=templates',
  ACCOUNT_SETTINGS: '/dashboard/account-settings',
  HELP: '/dashboard/help',
  CHANGELOG: '/dashboard/changelog',

  CLIENT_PORTAL_PUBLIC: '/client-portal/:shareToken',
  CLIENT_PORTAL_PUBLIC_BY_TOKEN: (shareToken: string) => `/client-portal/${shareToken}`,
  INVOICE_PUBLIC: '/invoice/:shareToken',
  INVOICE_PUBLIC_BY_TOKEN: (shareToken: string) => `/invoice/${shareToken}`,
  FORM_PUBLIC: '/form/:shareToken',
  FORM_PUBLIC_BY_TOKEN: (shareToken: string) => `/form/${shareToken}`,

  // App Gate (mobile browser redirect)
  APP_GATE: '/app',

  // Payment
  PAYMENT_SUCCESS: '/payment/success', // PLANNED: not yet routed
  PAYMENT_CANCEL: '/payment/cancel', // PLANNED: not yet routed
};
